import { Analysis, Plus, Search } from '@icon-park/react'

import { CompanyHealthCheckResponse, CompanyResponse, Paged, UserResponseRole } from '@shared/models'

import { Routes } from '@/configs'

import loadImg from '@/assets/images/load.gif'
import logoImg from '@/assets/images/logo.svg'
import { Tokens } from '@/assets/tokens'

import { Api } from '@/modules/api'

import { useRouting } from '@/hooks/useRouting'

import { Button, Divider, Flex, Input } from '@/components'
import { IconTooltip } from '@/components/core'
import { ServerError, SkeletonLoader, UserInfo } from '@/components/derived'

import { Auth } from '@/modules'
import { useEffect, useMemo, useState } from 'react'
import {
  Card,
  CardImage,
  CompanyName,
  Container,
  Content,
  ContentContainer,
  Header,
  HeaderLeft,
  HeaderRight,
  HealthCheck,
  Logo,
} from './Home.styles'

interface CompanyItemProps {
  name: string
  logoID?: string
  onClick: () => void
  health?: CompanyHealthCheckResponse
  hasCompetitors?: boolean
}

export type HealthStatus = 'success' | 'warning' | 'error'

const openInNewTab = (url: string) => {
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
  if (newWindow) newWindow.opener = null
}

export function CompanyItem({ name, logoID, onClick, health, hasCompetitors }: CompanyItemProps) {
  let url = loadImg
  if (logoID === 'default-logo-color') {
    url = process.env.REACT_APP_DEFAULT_LOGO_COLOR!
  } else if (logoID === 'default-logo-gray') {
    url = process.env.REACT_APP_DEFAULT_LOGO_GRAY!
  } else {
    const [isLoadingLogo, , logo] = Api.Query<string>(`files/${logoID}`)
    url = !isLoadingLogo && logo ? logo : loadImg
  }

  const status = useMemo(() => {
    if (!health || Object.keys(health).length === 0) {
      return 'success' as const
    }

    const keysToIgnore: string[] = ['isKeyMessagesChecked']

    if (!hasCompetitors) {
      keysToIgnore.push('isCompetitorsChecked')
    }

    const healthValues = Object.entries(health)
      .filter(([key]) => !keysToIgnore.includes(key))
      .map(([_, value]) => value)

    const isAllFalse = healthValues.every((value) => value === false)
    const isAllTrue = healthValues.every((value) => value === true)

    const currentStatus: HealthStatus =
      isAllFalse ? 'error'
      : isAllTrue ? 'success'
      : 'warning'

    return currentStatus
  }, [health, hasCompetitors])

  return (
    <Card onClick={onClick}>
      <CompanyName>{name}</CompanyName>
      <HealthCheck $status={status} />
      <CardImage alt={name} src={url} />
    </Card>
  )
}

export function Home() {
  const { navigate } = useRouting()

  const [fetchCompanies, isFetchingCompanies, companies, error] = Api.LazyQuery<Paged<CompanyResponse>>('companies')
  const [filteredCompanies, setFilteredCompanies] = useState<CompanyResponse[]>()
  const [searchTerm, setSearchTerm] = useState<string>('')

  function filterCompany() {
    return companies?.elements.filter((company) => company.name.toLowerCase().includes(searchTerm.toLowerCase()))
  }

  useEffect(() => {
    fetchCompanies({
      page: 1,
      size: 200,
      orderBy: 'name:ASC',
      loadRegular: true,
      loadHealthCheck: true,
      loadCompetitors: false,
    })
  }, [])

  useEffect(() => {
    if (searchTerm) {
      setFilteredCompanies(filterCompany())
      return
    }
    setFilteredCompanies(companies?.elements)
  }, [searchTerm])

  useEffect(() => {
    if (companies) {
      setFilteredCompanies(companies?.elements)
    }
  }, [companies])

  if (error) return <ServerError />

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <Logo alt="logo" src={logoImg} />
          <Input
            icon={Search}
            placeholder="Pesquise por uma empresa..."
            onChange={(value: string) => setSearchTerm(value)}
          />
        </HeaderLeft>
        <HeaderRight>
          <Flex $type="row" $justify="flex-end" $align="stretch" $gap={8}>
            <Button.Root onClick={() => openInNewTab('/#/dashboards')} $variant="outlined">
              <Button.Text value="Dashboard" />
            </Button.Root>
            <Auth.ProtectedComponent
              requiredRoles={[UserResponseRole.Admin, UserResponseRole.AdminMaster, UserResponseRole.Leadership]}>
              <Button.Root onClick={() => navigate(Routes.Companies)} $variant="outlined">
                <Button.Icon value={Plus} color={Tokens.ColorUIPrimary} />
                <Button.Text value="Registrar Empresa" />
              </Button.Root>
              <IconTooltip
                icon={
                  <Button.Root onClick={() => navigate(Routes.Cena)} $variant="outlined">
                    <Button.Icon value={Analysis} color={Tokens.ColorUIPrimaryDim} />
                  </Button.Root>
                }
                tooltipText="Configurar pesos de ICENA"
              />
            </Auth.ProtectedComponent>
            <Auth.ProtectedComponent requiredRoles={[UserResponseRole.AdminMaster]}>
              <Button.Root onClick={() => navigate(Routes.Users)} $variant="outlined">
                <Button.Text value="Gerenciar Usuários" />
              </Button.Root>
            </Auth.ProtectedComponent>
          </Flex>
          <UserInfo />
        </HeaderRight>
      </Header>
      <ContentContainer>
        <Divider.Root>
          <Divider.Title value="Selecione uma empresa" />
          <Divider.Spacer />
          <Divider.Subtitle value={`${filteredCompanies?.length ?? 0} registro(s)`} />
        </Divider.Root>
        <Content>
          {isFetchingCompanies ?
            <SkeletonLoader />
          : filteredCompanies?.map((company: CompanyResponse) => {
              return (
                <CompanyItem
                  key={company.id}
                  name={company.name}
                  logoID={company.systemLogoID}
                  health={company.healthCheck}
                  hasCompetitors={company.hasCompetitors}
                  onClick={() => navigate(Routes.CompanyDetails, company.id)}
                />
              )
            })
          }
        </Content>
      </ContentContainer>
    </Container>
  )
}
